import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, MapPin } from "lucide-react";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import TableSecondary from "../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import {
  CarrierScoreDetailsModal,
  formatScoreValue,
  renderScoreDetailsBreakdown,
} from "../../../utils/carrierScoreDetailsUi";
import { resourceSelectionApi } from "./api/resourceSelectionApi";
import {
  applySimulationKpiFactors,
  buildSimulationKpiColumns,
  buildSimulationScoreBreakdown,
  dedupeLocationSearchOptions,
  EMPTY_SIMULATION_STOP,
  filterLocationSearchOptions,
  filterSimulationLocationSearchResults,
  getSimulationKpiTableValue,
  isSimulationCarrierNameMissing,
  getTodayOperationDate,
  isSimulationStopValid,
  locationRowToSimulationStop,
  locationSearchDedupeKey,
  normalizeLocationSearchOption,
} from "./selectionSimulationUtils";
import { getAssignedOfficeCodes } from "../../../utils/assignedOffices";

const LOCATION_TYPEAHEAD_DEBOUNCE_MS = 300;
const LOCATION_TYPEAHEAD_MIN_CHARS = 2;
// Stable reference so the `accessibleOfficeCodes` default doesn't create a new
// array every render - a fresh [] here fed an effect dependency below and caused
// an infinite render loop (which in turn stalled route/tab navigation away from
// Resource Selection while the loop was running).
const EMPTY_ACCESSIBLE_OFFICE_CODES = [];

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const STOP_FIELD_LABELS = {
  locationName: "Location",
  addressLine1: "Address Line 1",
  addressLine2: "Address Line 2",
  city: "City",
  state: "State",
  zipCode: "ZIP Code",
  country: "Country",
};

function SimulationOperationDatePicker({ value, onChange }) {
  const dateValue = value || getTodayOperationDate();

  return (
    <div className="max-w-xs">
      <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
        Operation date <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          type="date"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm"
          value={dateValue}
          onChange={(event) => onChange(event.target.value || getTodayOperationDate())}
          onClick={(event) => event.currentTarget.showPicker?.()}
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          onClick={(event) => {
            const input = event.currentTarget.previousSibling;
            if (input instanceof HTMLInputElement) {
              input.showPicker?.();
            }
          }}
          aria-label="Open operation date picker"
        >
          <Calendar size={16} />
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Used for contract and rate validity (same role as pickup date on shipment rate shopping).
      </p>
    </div>
  );
}

function SimulationLocationTypeahead({
  locationName,
  officeCode,
  scope = "originator",
  accessibleOfficeCodes = EMPTY_ACCESSIBLE_OFFICE_CODES,
  onSelectStop,
  onLocationNameChange,
  placeholder = "Start typing a location name",
  helperText,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [apiMatches, setApiMatches] = useState([]);

  const isAllAccessibleScope = scope === "all-accessible";
  const resolvedAccessibleOfficeCodes = useMemo(() => {
    const fromProp = (Array.isArray(accessibleOfficeCodes) ? accessibleOfficeCodes : [])
      .map((code) => String(code || "").trim())
      .filter(Boolean);
    return fromProp.length ? fromProp : getAssignedOfficeCodes();
  }, [accessibleOfficeCodes]);

  const scopeKey = useMemo(() => {
    if (isAllAccessibleScope) return "all-accessible";
    return `originator:${(officeCode || "").trim()}`;
  }, [isAllAccessibleScope, officeCode]);

  const canSearch = isAllAccessibleScope || Boolean((officeCode || "").trim());

  useEffect(() => {
    if (!canSearch) {
      setCatalog([]);
      setLoadError(
        isAllAccessibleScope
          ? ""
          : "Select an originator before searching locations."
      );
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setCatalog([]);
    setApiMatches([]);

    (async () => {
      try {
        const response = await resourceSelectionApi.listLocationsGrouped();
        let rows = Array.isArray(response?.data) ? response.data : [];

        if (!isAllAccessibleScope) {
          const code = (officeCode || "").trim().toLowerCase();
          if (code) {
            rows = rows.filter((row) => {
              const rowOffice = String(row?.officeCode || row?.office_code || "")
                .trim()
                .toLowerCase();
              return rowOffice === code;
            });
          }
        }

        if (cancelled) return;

        const normalized = dedupeLocationSearchOptions(
          rows.map(normalizeLocationSearchOption).filter(Boolean)
        ).sort((a, b) => a.locationName.localeCompare(b.locationName));

        setCatalog(normalized);
        if (!normalized.length) {
          setLoadError("No locations found.");
        }
      } catch (error) {
        if (cancelled) return;
        setLoadError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Unable to load locations."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scopeKey, canSearch, isAllAccessibleScope, officeCode]);

  useEffect(() => {
    const query = (locationName || "").trim();
    if (query.length < LOCATION_TYPEAHEAD_MIN_CHARS) {
      setApiMatches([]);
      setSearching(false);
      return undefined;
    }

    const localHits = filterLocationSearchOptions(catalog, query, 1);
    if (localHits.length) {
      setApiMatches([]);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      (async () => {
        try {
          const response = await resourceSelectionApi.searchLocations({ locationName: query });
          if (cancelled) return;
          const matches = filterSimulationLocationSearchResults(response?.data, {
            officeCode,
            scope,
            accessibleOfficeCodes: resolvedAccessibleOfficeCodes,
            limit: 50,
          });
          setApiMatches(matches);
        } catch {
          if (!cancelled) setApiMatches([]);
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, LOCATION_TYPEAHEAD_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locationName, catalog, officeCode, scope, resolvedAccessibleOfficeCodes]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const visibleOptions = useMemo(() => {
    const local = filterLocationSearchOptions(catalog, locationName, 50);
    const merged = dedupeLocationSearchOptions([...local, ...apiMatches]).sort((a, b) =>
      a.locationName.localeCompare(b.locationName)
    );
    return dedupeLocationSearchOptions(merged).slice(0, 20);
  }, [catalog, apiMatches, locationName]);

  const defaultHelperText = isAllAccessibleScope
    ? "Type to search locations across all offices you can access."
    : "Type to search locations for the config originator office.";

  const selectOption = (option) => {
    if (!option) return;
    const nextStop = locationRowToSimulationStop(option.rawRow || option);
    onSelectStop?.({ ...nextStop, stopName: "" });
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleFocus = () => setOpen(true);

  const handleInputChange = (nextValue) => {
    onLocationNameChange?.(nextValue);
    setOpen(true);
    setActiveIdx(-1);
  };

  const handleKeyDown = (event) => {
    if (!open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIdx((idx) => Math.min(visibleOptions.length - 1, idx + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIdx((idx) => Math.max(0, idx - 1));
    } else if (event.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < visibleOptions.length) {
        event.preventDefault();
        selectOption(visibleOptions[activeIdx]);
      } else if (visibleOptions.length === 1) {
        event.preventDefault();
        selectOption(visibleOptions[0]);
      }
    } else if (event.key === "Escape" || event.key === "Tab") {
      setOpen(false);
    }
  };

  const trimmedQuery = (locationName || "").trim();
  const showDropdown = open && (canSearch || loadError);

  return (
    <div ref={rootRef} className="relative">
      <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
        {STOP_FIELD_LABELS.locationName}
      </label>
      <p className="mb-1 text-xs text-gray-500">{helperText || defaultHelperText}</p>
      <input
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={locationName}
        autoComplete="off"
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />

      {showDropdown ? (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {!canSearch ? (
            <li className="px-3 py-2 text-sm text-gray-500">
              Select an originator to search locations.
            </li>
          ) : null}

          {canSearch && loading ? (
            <li className="px-3 py-2 text-sm text-gray-500">Loading locations…</li>
          ) : null}

          {canSearch && !loading && searching ? (
            <li className="px-3 py-2 text-sm text-gray-500">Searching locations…</li>
          ) : null}

          {canSearch && !loading && !searching && loadError && !visibleOptions.length ? (
            <li className="px-3 py-2 text-sm text-gray-500">{loadError}</li>
          ) : null}

          {canSearch &&
            !loading &&
            visibleOptions.map((option, idx) => {
              const active = idx === activeIdx;
              const subtitle = [
                option.city,
                option.state,
                isAllAccessibleScope && option.officeCode ? option.officeCode : "",
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <li
                  key={locationSearchDedupeKey(option) || `${option.locationName}-${idx}`}
                  role="option"
                  aria-selected={active}
                  className={`cursor-pointer px-3 py-2 ${
                    active ? "bg-[#e8edff] text-[#223688]" : "hover:bg-gray-50"
                  }`}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  <div className="text-sm font-medium">{option.locationName}</div>
                  {subtitle ? <div className="text-xs text-gray-500">{subtitle}</div> : null}
                </li>
              );
            })}

          {canSearch &&
          !loading &&
          !searching &&
          trimmedQuery.length > 0 &&
          !visibleOptions.length ? (
            <li className="px-3 py-2 text-sm text-gray-500">No matching locations.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

/** Alias kept for hot-reload compatibility after rename from lookup to typeahead. */
const SimulationLocationLookup = SimulationLocationTypeahead;

function SimulationStopFields({
  title,
  stopTypeLabel,
  stop,
  officeCode,
  scope = "originator",
  onChange,
}) {
  const updateField = (field, value) => {
    onChange?.({ ...stop, [field]: value });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-[#f8fafc] p-fluid-2">
      <div className="mb-fluid-2 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1e2a74] text-white">
          <MapPin size={16} />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0c1629]">{title}</p>
          <p className="text-xs text-gray-500">{stopTypeLabel}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-fluid-2 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <SimulationLocationTypeahead
            locationName={stop.stopName || ""}
            officeCode={officeCode}
            scope={scope}
            onSelectStop={onChange}
            onLocationNameChange={(stopName) => updateField("stopName", stopName)}
            placeholder={`${title} location name`}
          />
        </div>

        {["addressLine1", "addressLine2", "city", "state", "zipCode", "country"].map((field) => (
          <div key={field}>
            <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
              {STOP_FIELD_LABELS[field]}
              {["city", "state", "zipCode", "country"].includes(field) ? (
                <span className="text-red-500"> *</span>
              ) : null}
            </label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={stop[field] || ""}
              onChange={(e) => updateField(field, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const simulationCarrierLabel = (row) => {
  const name = String(row?.carrierName || "").trim();
  if (name && !isSimulationCarrierNameMissing(row)) return name;
  return name || "—";
};

const SelectionSimulationPanel = ({
  strategies = [],
  officeCode = "",
  defaultOriginStop = EMPTY_SIMULATION_STOP,
  defaultDestinationStop = EMPTY_SIMULATION_STOP,
  isRunning = false,
  simulationResult = null,
  rankedCarriers = [],
  onRunSimulation,
  isConfigDirty = false,
}) => {
  const [originStop, setOriginStop] = useState(defaultOriginStop);
  const [destinationStop, setDestinationStop] = useState(defaultDestinationStop);
  const [operationDate, setOperationDate] = useState(getTodayOperationDate());
  const [scoreDetailsCarrier, setScoreDetailsCarrier] = useState(null);

  const defaultOriginStopKey = useMemo(
    () => JSON.stringify(defaultOriginStop || EMPTY_SIMULATION_STOP),
    [defaultOriginStop]
  );
  const defaultDestinationStopKey = useMemo(
    () => JSON.stringify(defaultDestinationStop || EMPTY_SIMULATION_STOP),
    [defaultDestinationStop]
  );

  useEffect(() => {
    setOriginStop(defaultOriginStop || { ...EMPTY_SIMULATION_STOP });
  }, [defaultOriginStopKey]);

  useEffect(() => {
    setDestinationStop(defaultDestinationStop || { ...EMPTY_SIMULATION_STOP });
  }, [defaultDestinationStopKey]);

  const rankedRows = useMemo(
    () =>
      [...(rankedCarriers.length ? rankedCarriers : simulationResult?.rankedCarriers || [])].sort(
        (a, b) => a.rank - b.rank
      ),
    [rankedCarriers, simulationResult?.rankedCarriers]
  );

  const strategyKpis = strategies[0]?.kpis || [];

  const enrichedRows = useMemo(() => {
    const withFactors = applySimulationKpiFactors(rankedRows, strategyKpis);
    return withFactors.map((row) => ({
      ...row,
      scoreBreakdown: buildSimulationScoreBreakdown(row, strategyKpis),
    }));
  }, [rankedRows, strategyKpis]);

  const openSimulationScoreDetails = (row) => {
    setScoreDetailsCarrier({
      carrierCode: row.carrierCode,
      carrierName: simulationCarrierLabel(row),
      __scoreBreakdown: buildSimulationScoreBreakdown(row, strategyKpis),
    });
  };

  const kpiColumns = useMemo(
    () => buildSimulationKpiColumns(enrichedRows, strategyKpis),
    [enrichedRows, strategyKpis]
  );

  const tableHeaders = useMemo(
    () => [
      "Rank",
      "Carrier",
      "Code",
      ...kpiColumns.map((column) => column.label),
      "KPI Factor",
      "Quoted total",
      "KPI total",
    ],
    [kpiColumns]
  );

  const tableColumnWidths = useMemo(() => {
    const fixed = ["6%", "16%", "9%"];
    const kpiWidth =
      kpiColumns.length > 0
        ? `${Math.max(8, Math.floor(40 / kpiColumns.length))}%`
        : null;
    const dynamic = kpiColumns.map(() => kpiWidth || "12%");
    return [...fixed, ...dynamic, "9%", "9%", "9%"];
  }, [kpiColumns]);

  const canRun =
    strategies.length > 0 &&
    Boolean((operationDate || "").trim()) &&
    isSimulationStopValid(originStop) &&
    isSimulationStopValid(destinationStop);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-fluid-3">
      <div className="mb-fluid-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Selection Simulation
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Pick a catalogue location or enter a full address (city and street). Simulation rate-shops
            using addresses when the location name is blank — same as planning a shipment with stop addresses.
            KPI factor, price, and cost come from the lane-paths ranking API (same source as shipment rate shopping).
            Use the same operation date as shipment pickup when comparing to a planned load.
          </p>
          {isConfigDirty ? (
            <p className="mt-1 text-sm text-amber-600">
              Save your configuration changes before running a simulation.
            </p>
          ) : null}
        </div>
        <Button
          text={isRunning ? "Running..." : "Run Selection Simulation"}
          onClick={() =>
            onRunSimulation?.({
              originStop,
              destinationStop,
              operationDate,
            })
          }
          disabled={isRunning || !canRun || isConfigDirty}
        />
      </div>

      <div className="mb-fluid-2">
        <SimulationOperationDatePicker value={operationDate} onChange={setOperationDate} />
      </div>

      <div className="mb-fluid-2 grid grid-cols-1 gap-fluid-2 lg:grid-cols-2">
        <SimulationStopFields
          title="Origin"
          stopTypeLabel="Pickup stop"
          stop={originStop}
          officeCode={officeCode}
          onChange={setOriginStop}
        />
        <SimulationStopFields
          title="Destination"
          stopTypeLabel="Drop-off stop"
          stop={destinationStop}
          scope="all-accessible"
          onChange={setDestinationStop}
        />
      </div>

      {enrichedRows.length ? (
        <div className="mt-fluid-3 overflow-x-auto">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
            Ranked Carriers
          </p>
          <TableSecondary
            headers={tableHeaders}
            columnWidths={tableColumnWidths}
            data={enrichedRows}
            renderRow={(row) => (
              <>
                <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">{row.rank}</td>
                <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{simulationCarrierLabel(row)}</td>
                <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.carrierCode || "—"}</td>
                {kpiColumns.map((column) => (
                  <td
                    key={`${row.carrierCode || row.rank}-${column.key}`}
                    className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums"
                    title={column.label}
                  >
                    {formatScoreValue(getSimulationKpiTableValue(row, column.key))}
                  </td>
                ))}
                <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                  {row.kpiFactor != null && row.kpiFactor !== "" ? (
                    <button
                      type="button"
                      className="text-[#223688] underline hover:text-[#1a2a66]"
                      onClick={() => openSimulationScoreDetails(row)}
                    >
                      {formatScoreValue(row.kpiFactor)}
                    </button>
                  ) : (
                    formatScoreValue(row.kpiFactor)
                  )}
                </td>
                <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                  {formatMoney(row.price)}
                </td>
                <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                  {formatMoney(row.cost)}
                </td>
              </>
            )}
          />
        </div>
      ) : null}

      <CarrierScoreDetailsModal
        carrier={scoreDetailsCarrier}
        onClose={() => setScoreDetailsCarrier(null)}
      >
        {scoreDetailsCarrier?.__scoreBreakdown
          ? renderScoreDetailsBreakdown(scoreDetailsCarrier.__scoreBreakdown)
          : null}
      </CarrierScoreDetailsModal>
    </div>
  );
};

export default SelectionSimulationPanel;
